import { PersonalScheduleProgramHistory, Program } from '@/domain'
import { AppInitialisation } from '@/domain/Sharing'
import composeId from '@/lib/api/local'
import { v1Exercise } from '@/lib/api/pocket'
import { exercisesMap } from '@/components/base/staticdata'

export interface IAsyncStorage {
    getItem(key: string): Promise<string | null>
    setItem(key: string, value: string): Promise<void>
    removeItem(key: string): Promise<void>
    keys(): Promise<string[]>
}

class LocalAsyncStorage implements IAsyncStorage {
    async getItem(key: string): Promise<string | null> {
        return localStorage.getItem(key)
    }
    async setItem(key: string, value: string): Promise<void> {
        localStorage.setItem(key, value)
    }
    async removeItem(key: string): Promise<void> {
        localStorage.removeItem(key)
    }
    async keys(): Promise<string[]> {
        const keys: string[] = []
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key !== null) keys.push(key)
        }
        return keys
    }
}

const BOOKMARK_LIST_PREFIX = 'bookmark-list'
const DRAFT_PROGRAM_PREFIX = 'draft-program'
const APP_INIT_PREFIX = 'v1.appInit'

export class LocalStorageManager {
    private storage: IAsyncStorage

    constructor(storage?: IAsyncStorage) {
        this.storage = storage || new LocalAsyncStorage()
    }

    private getCurrentPrefix(): string {
        const d = new Date()
        return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
    }

    // Date-prefixed cache keys rotate daily and would otherwise accumulate
    // forever — Telegram CloudStorage caps at 1024 keys, so stale entries
    // must be dropped when a fresh one is written. Keys may be stored in
    // sanitized form (TelegramAsyncStorage strips characters like '.'), so
    // both raw and sanitized variants are matched.
    private async removeStaleDatedEntries(basePrefix: string): Promise<void> {
        const sanitize = (key: string) => key.replace(/[^A-Za-z0-9_-]/g, '')
        const current = composeId(basePrefix, this.getCurrentPrefix())
        const bases = [basePrefix, sanitize(basePrefix)]
        const keepers = [current, sanitize(current)]
        try {
            const keys = await this.storage.keys()
            await Promise.all(
                keys
                    .filter(
                        (key) =>
                            bases.some((base) => key.startsWith(base)) &&
                            !keepers.some((keep) => key.startsWith(keep))
                    )
                    .map((key) => this.storage.removeItem(key))
            )
        } catch (error) {
            console.error('Failed to clean stale cache entries:', error)
        }
    }

    public async getExercises(): Promise<exercisesMap> {
        const data = await this.storage.getItem('v1.v1Exercise')
        if (!data) return {}
        return JSON.parse(data) as exercisesMap
    }

    public async saveExercises(exercises: v1Exercise[]): Promise<void> {
        const exerciseMap: exercisesMap = exercises.reduce((map, exercise) => {
            map[exercise.id] = exercise
            map[exercise.slug] = exercise
            return map
        }, {} as exercisesMap)
        await this.storage.setItem('v1.v1Exercise', JSON.stringify(exerciseMap))
    }

    public async getOngoingWorkout(
        version: string
    ): Promise<[string, PersonalScheduleProgramHistory | undefined]> {
        const keys = await this.storage.keys()
        for (const key of keys) {
            if (key.includes(version)) {
                const data = await this.storage.getItem(key)
                if (data) {
                    return [
                        key.split('.')[1],
                        JSON.parse(data) as PersonalScheduleProgramHistory
                    ]
                }
            }
        }
        return ['', undefined]
    }

    public async getLatestTrainingSet(
        version: string,
        scheduleId: string
    ): Promise<PersonalScheduleProgramHistory | undefined> {
        const data = await this.storage.getItem(composeId(version, scheduleId))
        if (data) return JSON.parse(data) as PersonalScheduleProgramHistory
        return undefined
    }

    public async saveLatestTrainingSet(
        version: string,
        scheduleId: string,
        data: PersonalScheduleProgramHistory
    ): Promise<void> {
        await this.storage.setItem(
            composeId(version, scheduleId),
            JSON.stringify(data)
        )
    }

    public async flushLatestTrainingSet(
        version: string,
        scheduleId: string
    ): Promise<void> {
        await this.storage.removeItem(composeId(version, scheduleId))
    }

    /**
     * Clears in-progress exercise local state for a programme.
     * Keys are `localVersion.programId.scheduleId` (composeId).
     */
    public async flushProgramTrainingSets(programId: string): Promise<void> {
        if (!programId) return
        const needle = `.${programId}.`
        const keys = await this.storage.keys()
        await Promise.all(
            keys
                .filter((key) => key.includes(needle))
                .map((key) => this.storage.removeItem(key))
        )
    }

    public async getFollowingList(): Promise<string[]> {
        const data = await this.storage.getItem(composeId('v1', 'following'))
        if (data) return JSON.parse(data) as string[]
        return []
    }

    public async saveFollowingList(data: string[]): Promise<void> {
        await this.storage.setItem(
            composeId('v1', 'following'),
            JSON.stringify(data)
        )
    }

    /**
     * Read-modify-write against the list stored right now, so concurrent
     * controls never overwrite each other with a stale captured snapshot.
     */
    public async toggleFollowing(id: string): Promise<string[]> {
        const current = await this.getFollowingList()
        const next = current.includes(id)
            ? current.filter((e) => e !== id)
            : [...current, id]
        await this.saveFollowingList(next)
        return next
    }

    public async getSubscriptionInfo(subKind: string): Promise<any> {
        const prefix = this.getCurrentPrefix()
        const data = await this.storage.getItem(
            composeId('v1', 'subscription-info', prefix, subKind)
        )
        if (data) return JSON.parse(data)
        return undefined
    }

    public async saveSubscriptionInfo(
        subKind: string,
        data: any
    ): Promise<void> {
        const prefix = this.getCurrentPrefix()
        await this.storage.setItem(
            composeId('v1', 'subscription-info', prefix, subKind),
            JSON.stringify(data)
        )
        await this.removeStaleDatedEntries(composeId('v1', 'subscription-info'))
    }

    public async getLessonAcquireInfo(lessonId: string): Promise<any> {
        const prefix = this.getCurrentPrefix()
        const data = await this.storage.getItem(
            composeId('v1', 'lesson-acquire-info', prefix, lessonId)
        )
        if (data) return JSON.parse(data)
        return undefined
    }

    public async saveLessonAcquireInfo(
        lessonId: string,
        data: any
    ): Promise<void> {
        const prefix = this.getCurrentPrefix()
        await this.storage.setItem(
            composeId('v1', 'lesson-acquire-info', prefix, lessonId),
            JSON.stringify(data)
        )
        await this.removeStaleDatedEntries(
            composeId('v1', 'lesson-acquire-info')
        )
    }

    public async toggleProgramBookmark(
        version: string,
        id: string
    ): Promise<void> {
        const bookmarks = await this.getProgramBookmarkList(version)
        const index = bookmarks.indexOf(id)
        if (index > -1) {
            bookmarks.splice(index, 1)
        } else {
            bookmarks.push(id)
        }
        await this.storage.setItem(
            composeId(version, BOOKMARK_LIST_PREFIX),
            JSON.stringify(bookmarks)
        )
    }

    public async getProgramBookmarkList(version: string): Promise<string[]> {
        const rawData = await this.storage.getItem(
            composeId(version, BOOKMARK_LIST_PREFIX)
        )
        if (!rawData) return []
        return JSON.parse(rawData) as string[]
    }

    public async saveDraftProgram(id: string, program: Program): Promise<void> {
        await this.storage.setItem(
            composeId(DRAFT_PROGRAM_PREFIX, id),
            JSON.stringify(program)
        )
    }

    public async retrieveDraftProgram(
        id: string
    ): Promise<Program | undefined> {
        const rawData = await this.storage.getItem(
            composeId(DRAFT_PROGRAM_PREFIX, id)
        )
        if (rawData) return JSON.parse(rawData) as Program
        return undefined
    }

    public async flushDraftProgram(id: string): Promise<void> {
        await this.storage.removeItem(composeId(DRAFT_PROGRAM_PREFIX, id))
    }

    public async saveInitState(data: AppInitialisation): Promise<void> {
        await this.storage.setItem(APP_INIT_PREFIX, JSON.stringify(data))
    }

    public async retrieveInitState(): Promise<AppInitialisation | undefined> {
        const rawData = await this.storage.getItem(APP_INIT_PREFIX)
        if (rawData) return JSON.parse(rawData) as AppInitialisation
        return undefined
    }

    public async flushInitState(): Promise<void> {
        await this.storage.removeItem(APP_INIT_PREFIX)
    }
}
